/**
 * Thin wrappers around the orval-generated proven-task file hooks: one
 * proven task's case files and one file's text (TRA-1477). Read-only.
 * Same unwrap pattern as useTrainingRuns.ts (orval v8 FETCH client returns
 * {data, status, headers}). The reads fetch only while `enabled`, so a
 * closed row costs nothing. The list of tasks itself comes from the tree
 * in useRefineryTree.ts; GET /api/proven-tasks stays for the training
 * agent.
 */

import type { ApiRequestError } from '@/api/client'
import {
  useListProvenTaskFilesApiProvenTasksFilesGet,
  useReadProvenTaskFileApiProvenTasksFileGet,
} from '@/api/generated/proven-tasks/proven-tasks'
import type { ProvenTaskFiles, TrainingAgentFile } from '@/api/generated/model'
import type { TaskCase } from '@/components/refinery/rows'

interface UnwrappedQuery<TData> {
  data: TData | undefined
  isLoading: boolean
  error: ApiRequestError | null
  isError: boolean
}

/** What names a task's case to the api. */
function caseParams(task: TaskCase): { batch: string; record_id: string; case: string } {
  return { batch: task.batch, record_id: task.record_id, case: task.case }
}

export function useProvenTaskFiles(task: TaskCase, enabled: boolean): UnwrappedQuery<ProvenTaskFiles> {
  const query = useListProvenTaskFilesApiProvenTasksFilesGet(caseParams(task), {
    query: { enabled, retry: false },
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: enabled && query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

export function useProvenTaskFile(
  task: TaskCase,
  path: string,
  enabled: boolean,
): UnwrappedQuery<TrainingAgentFile> {
  const query = useReadProvenTaskFileApiProvenTasksFileGet(
    { ...caseParams(task), path },
    { query: { enabled, retry: false } },
  )
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: enabled && query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}
